import { useAtomValue } from 'jotai';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';
import { PhotoAvatar, Skeleton, Tag, Text, cn } from '@growtherapy/sprout-ui';

import { useGetTelehealthSessionInfo } from '../../hooks';
import { Provider } from '../../types';
import { getLocalizedPronoun } from '../../utils';
import { providerIsOnlineAtom } from '../state';

interface WrapperPassThroughProps {
  className?: string;
  label?: string;
  hideStatus?: boolean;
}
export interface ProviderInfoSectionProps extends WrapperPassThroughProps {
  provider?: Pick<Provider, 'image' | 'name' | 'pronouns'>;
  loading?: boolean;
  providerIsOnline: boolean | null;
}

function ProviderStatusTag({ providerIsOnline }: { providerIsOnline: ProviderInfoSectionProps['providerIsOnline'] }) {
  const t = useTranslate();
  if (providerIsOnline === null) return null;

  const tagColor = providerIsOnline ? 'green' : 'neutral';
  const tagLabel = providerIsOnline ? t('telehealth-core:core.available') : t('telehealth-core:core.offline');

  return (
    // Match tag container height to line height of h1 text for provider name.
    <div className='flex items-center h-7'>
      <Tag use={tagColor}>
        <span className='sr-only normal-case'>{t('telehealth-core:waitingRoom.yourProviderIsCurrently')}&nbsp;</span>
        {tagLabel}
      </Tag>
    </div>
  );
}

export function ProviderInfoSection({ className, provider, loading, providerIsOnline }: ProviderInfoSectionProps) {
  const t = useTranslate();

  const providerAltLabel = provider?.name
    ? t('telehealth-core:providerInfo.providerName_dynamic', {
        providerName: provider.name,
      })
    : t('telehealth-core:providerInfo.providerPicture');

  return (
    <div data-testid='provider-info-section' className={cn('relative inset-0 ml-3 self-start p-4', className)}>
      {loading ? (
        <div className='flex items-center'>
          <span data-testid='provider-info-section.image-skeleton'>
            <Skeleton className='my-0 mx-0 rounded-full w-12 h-12 sm:w-18 sm:h-18' />
          </span>
          <div className='flex flex-col justify-center ml-2'>
            <span data-testid='provider-info-section.waiting-room-skeleton'>
              <Skeleton className='my-0 mx-0 h-5 w-28' />
            </span>
            <span data-testid='provider-info-section.name-skeleton'>
              <Skeleton className='my-0 mx-0 w-60 mt-2' />
            </span>
            <span data-testid='provider-info-section.status-skeleton'>
              <Skeleton className='my-0 mx-0 h-6 w-20 mt-2 rounded-full' />
            </span>
          </div>
        </div>
      ) : (
        <div className='flex items-center'>
          <div className='sm:hidden fs-exclude' data-dd-privacy='mask' data-testid='provider-info-section.image'>
            <PhotoAvatar alt={providerAltLabel} src={provider?.image ?? ''} size='sm' />
          </div>
          <div className='hidden sm:block fs-exclude' data-dd-privacy='mask' data-testid='provider-info-section.image'>
            <PhotoAvatar alt={providerAltLabel} src={provider?.image ?? ''} size='md' />
          </div>
          <div className='flex items-end gap-x-2 ml-2 flex-wrap'>
            <Text
              as='h1'
              variant='sm'
              data-testid='provider-info-section.provider-waiting-room'
              className='rebrand:text-text-inverse'
            >
              {t('telehealth-core:waitingRoom.waitingRoomOf')}
              <Text
                as='span'
                data-testid='provider-info-section.provider-name'
                variant='lg'
                className='block text-lilac-700 rebrand:text-text-inverse font-medium text-xl leading-8 fs-exclude'
                data-dd-privacy='mask'
              >
                {provider?.name}
                {(provider?.pronouns?.length ?? 0) > 0 && (
                  <span className='font-normal' data-testid='provider-info-section.provider-pronouns'>
                    &nbsp;(
                    {getLocalizedPronoun(t, provider?.pronouns![0] ?? '')})
                  </span>
                )}
              </Text>
            </Text>

            <ProviderStatusTag providerIsOnline={providerIsOnline} />
          </div>
        </div>
      )}
    </div>
  );
}

export function ProviderInfoSectionWrapper(props: WrapperPassThroughProps) {
  const { providerShortId, patientShortId } = useParams();
  const { data, loading } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const providerIsOnline = useAtomValue(providerIsOnlineAtom);
  const provider = data?.telehealthSessionInfo?.provider;

  return <ProviderInfoSection {...props} loading={loading} provider={provider} providerIsOnline={providerIsOnline} />;
}
